import { AxiosError } from "axios";

// Error helpers for the SAP API. Errors come back as { message, code }, { message, code, reason }
// or, for zod validation, { errors: [{ path, message }], code: "VALIDATION_FAILED" }.

interface ApiErrorBody {
  message?: string;
  error?: string;
  code?: string;
  reason?: string;
  errors?: Array<{ path?: string; message?: string }>;
}

const responseOf = (err: unknown) => (err as AxiosError<unknown> | null | undefined)?.response;

const bodyOf = (err: unknown): ApiErrorBody | null => {
  const data = responseOf(err)?.data;
  if (data && typeof data === "object") {
    return data as ApiErrorBody;
  }
  if (typeof data === "string" && data.trim()) {
    return { message: data };
  }
  return null;
};

export const getApiErrorStatus = (err: unknown): number | null => responseOf(err)?.status ?? null;

export const getApiErrorCode = (err: unknown): string | null => bodyOf(err)?.code ?? null;

export const getApiErrorReason = (err: unknown): string | null => bodyOf(err)?.reason ?? null;

const describeValidationErrors = (errors: ApiErrorBody["errors"]): string => {
  if (!Array.isArray(errors)) {
    return "";
  }
  return errors
    .map((entry) => {
      const path = entry.path ? `${entry.path.replace(/\.(\d+)\./g, "[$1].")}: ` : "";
      return entry.message ? `${path}${entry.message}` : "";
    })
    .filter(Boolean)
    .join("; ");
};

/** Human-readable message for a failed API call (the API's `message` when it sent one). */
export const getApiErrorMessage = (
  err: unknown,
  fallback = "Something went wrong. Please try again."
): string => {
  const body = bodyOf(err);
  if (body) {
    if (body.message && body.message !== "Validation failed") {
      return body.message;
    }
    const details = describeValidationErrors(body.errors);
    if (details) {
      return details;
    }
    if (body.error) {
      return body.error;
    }
  }
  if (!responseOf(err) && err instanceof Error && err.message) {
    return err.message === "Network Error"
      ? "Could not reach the server. Check your connection and try again."
      : err.message;
  }
  return fallback;
};

/** react-query retry policy: never retry 4xx answers, retry other failures twice. */
export const shouldRetryRequest = (failureCount: number, err: unknown): boolean => {
  const status = getApiErrorStatus(err);
  if (status !== null && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < 2;
};
